<script setup>
import { computed, nextTick, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';

import ApiClient from 'dashboard/api/ApiClient';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

import {
  createUnofficialCampaign,
  getUnofficialCampaign,
  normalizePhoneNumber,
  normalizeUnofficialCampaign,
  toDatetimeLocal,
  toTimeString,
  updateUnofficialCampaign,
} from '../assets/unofficialCampaigns.service.js';

const emit = defineEmits(['close', 'saved']);

const store = useStore();
const { t } = useI18n();

const DEFAULT_DAY_KEYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MEDIA_TYPES = {
  image: {
    extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'],
    mimeTypes: [
      'image/jpeg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
    ],
  },
  video: {
    extensions: ['mp4', '3gp', 'mov', 'avi', 'mkv', 'webm'],
    mimeTypes: ['video/mp4', 'video/3gpp', 'video/quicktime'],
  },
  document: {
    extensions: ['pdf', 'doc', 'docx', 'xls', 'xlsx'],
    mimeTypes: [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
  },
  audio: {
    extensions: ['mp3', 'ogg', 'wav', 'aac', 'm4a'],
    mimeTypes: ['audio/mpeg', 'audio/ogg', 'audio/wav', 'audio/aac'],
  },
  sticker: {
    extensions: ['webp', 'gif'],
    mimeTypes: ['image/webp', 'image/gif'],
  },
};

const createEmptyForm = account => ({
  nome: '',
  mensagem: '',
  horario_disparo: '',
  dt_inicio: '',
  dt_fim: '',
  account: account || '',
  inbox: '',
  media_type: 'image',
  mime_type: 'image/jpeg',
  link_media: '',
});

const createEmptyContact = () => ({
  name: '',
  phone: '',
  vars: {},
});

const dialogRef = ref(null);
const csvInputRef = ref(null);

const mode = ref('create');
const campaignId = ref(null);
const form = ref(createEmptyForm(''));
const selectedDayKeys = ref([...DEFAULT_DAY_KEYS]);
const contacts = ref([]);
const newContact = ref(createEmptyContact());
const inboxes = ref([]);
const isCSVMode = ref(false);
const isInitializing = ref(false);
const isSaving = ref(false);
const formAlert = ref({ type: '', message: '' });

const accountId = computed(() => store.getters.getCurrentAccountId);
const isEditMode = computed(() => mode.value === 'edit');
const isBusy = computed(() => isInitializing.value || isSaving.value);

const dialogTitle = computed(() => {
  if (isEditMode.value) return t('CAMPAIGN.UNOFFICIAL.EDIT.TITLE');
  return t('CAMPAIGN.UNOFFICIAL.CREATE.TITLE');
});

const submitLabel = computed(() => {
  if (isEditMode.value) return t('CAMPAIGN.UNOFFICIAL.BUTTONS.SAVE');
  return t('CAMPAIGN.UNOFFICIAL.BUTTONS.CREATE');
});

const weekdayOptions = computed(() => [
  { key: 'Sun', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.SUN') },
  { key: 'Mon', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.MON') },
  { key: 'Tue', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.TUE') },
  { key: 'Wed', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.WED') },
  { key: 'Thu', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.THU') },
  { key: 'Fri', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.FRI') },
  { key: 'Sat', label: t('CAMPAIGN.UNOFFICIAL.FORM.DAYS.SAT') },
]);

const detectedVariableKeys = computed(() => {
  const variables = new Set();
  const regex = /\{\s*(\d+)\s*\}/g;
  let match = regex.exec(form.value.mensagem || '');

  while (match) {
    variables.add(match[1]);
    match = regex.exec(form.value.mensagem || '');
  }

  return Array.from(variables).sort(
    (left, right) => Number(left) - Number(right)
  );
});

const formAlertClass = computed(() => {
  if (formAlert.value.type === 'success') {
    return 'border-n-teal-6 bg-n-teal-1 text-n-teal-11';
  }

  return 'border-n-ruby-6 bg-n-ruby-2 text-n-ruby-11';
});

watch(detectedVariableKeys, keys => {
  const nextVariables = {};

  keys.forEach(key => {
    nextVariables[key] = newContact.value.vars?.[key] || '';
  });

  newContact.value = {
    ...newContact.value,
    vars: nextVariables,
  };
});

const getInboxes = async currentAccountId => {
  if (!currentAccountId) return [];

  try {
    const client = new ApiClient(`accounts/${currentAccountId}/inboxes`);
    const response = await client.get();
    const payload = response?.data;
    let list = [];

    if (Array.isArray(payload?.payload)) {
      list = payload.payload;
    } else if (Array.isArray(payload)) {
      list = payload;
    }

    return list.map(inbox => ({
      id: inbox.id,
      name: inbox.name,
    }));
  } catch {
    return [];
  }
};

const showFormAlert = (type, message) => {
  formAlert.value = { type, message };
};

const clearFormAlert = () => {
  formAlert.value = { type: '', message: '' };
};

const resetState = () => {
  form.value = createEmptyForm(accountId.value);
  selectedDayKeys.value = [...DEFAULT_DAY_KEYS];
  contacts.value = [];
  newContact.value = createEmptyContact();
  isCSVMode.value = false;
  clearFormAlert();

  if (csvInputRef.value) {
    csvInputRef.value.value = '';
  }
};

const loadInboxes = async () => {
  inboxes.value = await getInboxes(accountId.value);
};

const loadCampaign = async id => {
  try {
    const campaign = normalizeUnofficialCampaign(
      await getUnofficialCampaign(id)
    );

    form.value = {
      nome: campaign.nome || '',
      mensagem: campaign.mensagem || '',
      horario_disparo: String(campaign.horario_disparo || '').slice(0, 5),
      dt_inicio: toDatetimeLocal(campaign.dt_inicio),
      dt_fim: toDatetimeLocal(campaign.dt_fim),
      account: campaign.account || accountId.value || '',
      inbox: campaign.inbox ? String(campaign.inbox) : '',
      media_type: campaign.media_type || 'image',
      mime_type: campaign.mime_type || 'image/jpeg',
      link_media: campaign.link_media || '',
    };

    selectedDayKeys.value = campaign.dias_semana?.length
      ? campaign.dias_semana
      : [...DEFAULT_DAY_KEYS];

    contacts.value = (campaign.lista_contato || []).map(contact => ({
      name: contact.name || '',
      phone: contact.numero || '',
      vars: contact.variaveis || {},
    }));
  } catch {
    showFormAlert('error', t('CAMPAIGN.UNOFFICIAL.EDIT.LOAD_ERROR'));
  }
};

const inferMediaType = url => {
  if (!url) return null;

  try {
    const extension = new URL(url.trim()).pathname
      .split('.')
      .pop()
      .toLowerCase()
      .split('?')[0];

    return Object.entries(MEDIA_TYPES).reduce((result, [type, config]) => {
      if (result) return result;

      const extensionIndex = config.extensions.indexOf(extension);
      if (extensionIndex === -1) return null;

      return {
        media_type: type,
        mime_type: config.mimeTypes[extensionIndex],
      };
    }, null);
  } catch {
    return null;
  }
};

const handleMediaLinkChange = () => {
  const result = inferMediaType(form.value.link_media);

  if (!result) return;

  form.value.media_type = result.media_type;
  form.value.mime_type = result.mime_type;
};

const toggleDay = dayKey => {
  selectedDayKeys.value = selectedDayKeys.value.includes(dayKey)
    ? selectedDayKeys.value.filter(key => key !== dayKey)
    : [...selectedDayKeys.value, dayKey];
};

const hasSelectedDay = dayKey => selectedDayKeys.value.includes(dayKey);

const checkContactVariablesComplete = contact =>
  detectedVariableKeys.value.every(key => contact.vars?.[key]?.trim());

const clearNewContactFields = () => {
  newContact.value = createEmptyContact();
  detectedVariableKeys.value.forEach(key => {
    newContact.value.vars[key] = '';
  });
};

const formatVariableToken = variable => `{${variable}}`;

const formatContactVariableToken = (key, value) => `{${key}}: ${value}`;

const addContact = () => {
  const name = newContact.value.name.trim();
  const phone = newContact.value.phone.trim();

  if (!phone) {
    showFormAlert('error', t('CAMPAIGN.UNOFFICIAL.ALERTS.PHONE_REQUIRED'));
    return;
  }

  const missingVariableKeys = detectedVariableKeys.value.filter(
    key => !newContact.value.vars?.[key]?.trim()
  );

  if (missingVariableKeys.length) {
    showFormAlert(
      'error',
      t('CAMPAIGN.UNOFFICIAL.ALERTS.VARIABLES_REQUIRED', {
        variables: missingVariableKeys.map(key => `{${key}}`).join(', '),
      })
    );
    return;
  }

  const variables = {};

  detectedVariableKeys.value.forEach(key => {
    variables[key] = newContact.value.vars?.[key]?.trim() || '';
  });

  contacts.value.push({
    name,
    phone: normalizePhoneNumber(phone),
    vars: variables,
  });

  clearNewContactFields();
  showFormAlert('success', t('CAMPAIGN.UNOFFICIAL.ALERTS.CONTACT_ADDED'));
};

const removeContact = index => {
  contacts.value = contacts.value.filter(
    (_, currentIndex) => currentIndex !== index
  );
};

const parseCSV = text => {
  const lines = text.split(/\r?\n/).filter(line => line.trim() !== '');

  if (lines.length < 2) {
    throw new Error(t('CAMPAIGN.UNOFFICIAL.CSV.INVALID_FILE'));
  }

  const delimiter =
    (lines[0].match(/;/g) || []).length > (lines[0].match(/,/g) || []).length
      ? ';'
      : ',';

  const parseLine = line => {
    const values = [];
    const buffer = [];
    let isInsideQuotes = false;

    for (let index = 0; index < line.length; index += 1) {
      const character = line[index];

      if (character === '"') {
        isInsideQuotes = !isInsideQuotes;
      } else if (character === delimiter && !isInsideQuotes) {
        values.push(buffer.join('').trim());
        buffer.length = 0;
      } else {
        buffer.push(character);
      }
    }

    values.push(buffer.join('').trim());
    return values;
  };

  const headers = parseLine(lines[0]).map(header =>
    header.toLowerCase().replace(/['"]/g, '').trim()
  );
  const phoneIndex = headers.findIndex(header =>
    ['phone', 'telefone', 'celular', 'whatsapp'].includes(header)
  );

  if (phoneIndex === -1) {
    throw new Error(t('CAMPAIGN.UNOFFICIAL.CSV.MISSING_PHONE_COLUMN'));
  }

  let importedCount = 0;

  lines
    .slice(1)
    .map(parseLine)
    .forEach(row => {
      const phone = row[phoneIndex]?.trim();

      if (!phone) return;

      const variables = {};

      detectedVariableKeys.value.forEach(key => {
        const variableIndex = headers.findIndex(
          header => header === `var_${key}` || header === `{${key}}`
        );

        if (variableIndex !== -1) {
          variables[key] = row[variableIndex]?.trim() || '';
        }
      });

      contacts.value.push({
        name: '',
        phone: normalizePhoneNumber(phone),
        vars: variables,
      });

      importedCount += 1;
    });

  return importedCount;
};

const handleCSVUpload = event => {
  const file = event.target.files?.[0];

  if (!file) return;

  const reader = new FileReader();
  reader.onload = loadEvent => {
    try {
      const importedCount = parseCSV(String(loadEvent.target?.result || ''));

      showFormAlert(
        'success',
        t('CAMPAIGN.UNOFFICIAL.CSV.IMPORT_SUCCESS', {
          count: importedCount,
        })
      );
    } catch (error) {
      showFormAlert('error', error.message);
    }
  };

  reader.readAsText(file, 'utf-8');
};

const downloadTemplateCSV = () => {
  const headers = ['name', 'phone'];
  detectedVariableKeys.value.forEach(key => headers.push(`var_${key}`));

  const rows = [
    headers.join(','),
    ...Array.from({ length: 3 }, (_, index) => {
      const row = [`Name ${index + 1}`, `+55${32999990000 + index}`];
      detectedVariableKeys.value.forEach(key =>
        row.push(`value_${key}_${index + 1}`)
      );
      return row.join(',');
    }),
  ];

  const blob = new Blob([rows.join('\n')], { type: 'text/csv' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'unofficial-campaign-contacts-template.csv';
  link.click();
  URL.revokeObjectURL(link.href);
};

const openCsvPicker = () => {
  csvInputRef.value?.click();
};

const tryAutoAddPendingContact = () => {
  if (contacts.value.length > 0) return;

  const hasPendingContact =
    newContact.value.name.trim() ||
    newContact.value.phone.trim() ||
    detectedVariableKeys.value.some(key =>
      newContact.value.vars?.[key]?.trim()
    );

  if (hasPendingContact) addContact();
};

const validateForm = () => {
  if (
    !form.value.nome.trim() ||
    !form.value.mensagem.trim() ||
    !form.value.horario_disparo ||
    !form.value.dt_inicio ||
    !form.value.dt_fim ||
    !form.value.inbox
  ) {
    showFormAlert('error', t('CAMPAIGN.UNOFFICIAL.ALERTS.REQUIRED_FIELDS'));
    return false;
  }

  const startAt = new Date(form.value.dt_inicio);
  const endAt = new Date(form.value.dt_fim);

  if (endAt <= startAt) {
    showFormAlert(
      'error',
      t('CAMPAIGN.UNOFFICIAL.ALERTS.INVALID_PERIOD')
    );
    return false;
  }

  const [hour, minute, second = 0] = String(form.value.horario_disparo)
    .split(':')
    .map(Number);
  const nextScheduledAt = new Date(startAt);
  nextScheduledAt.setHours(hour, minute, second, 0);

  if (nextScheduledAt < startAt) {
    nextScheduledAt.setDate(nextScheduledAt.getDate() + 1);
  }

  const hasScheduledOccurrence = Array.from({ length: 8 }).some(() => {
    const dayKey = DEFAULT_DAY_KEYS[nextScheduledAt.getDay()];
    const occursWithinPeriod =
      nextScheduledAt <= endAt && selectedDayKeys.value.includes(dayKey);

    nextScheduledAt.setDate(nextScheduledAt.getDate() + 1);
    return occursWithinPeriod;
  });

  if (!hasScheduledOccurrence) {
    showFormAlert(
      'error',
      t('CAMPAIGN.UNOFFICIAL.ALERTS.NO_SCHEDULE_IN_PERIOD')
    );
    return false;
  }

  return true;
};

const buildPayload = () => ({
  nome: form.value.nome.trim(),
  mensagem: form.value.mensagem.trim(),
  horario_disparo: toTimeString(form.value.horario_disparo),
  dt_inicio: form.value.dt_inicio,
  dt_fim: form.value.dt_fim,
  lista_contato: contacts.value.map(contact => ({
    numero: contact.phone,
    variaveis: contact.vars,
  })),
  dias_semana: selectedDayKeys.value,
  account: form.value.account,
  inbox: form.value.inbox,
  media_type: form.value.media_type,
  mime_type: form.value.mime_type,
  link_media: form.value.link_media || null,
});

const handleSubmit = async () => {
  clearFormAlert();
  tryAutoAddPendingContact();

  if (!validateForm()) return;

  if (!contacts.value.length) {
    showFormAlert('error', t('CAMPAIGN.UNOFFICIAL.ALERTS.CONTACTS_REQUIRED'));
    return;
  }

  const incompleteContacts = contacts.value.filter(
    contact => !checkContactVariablesComplete(contact)
  );

  if (incompleteContacts.length > 0) {
    showFormAlert(
      'error',
      t('CAMPAIGN.UNOFFICIAL.ALERTS.CONTACT_VARIABLES_MISSING', {
        count: incompleteContacts.length,
      })
    );
    return;
  }

  isSaving.value = true;

  try {
    if (isEditMode.value && campaignId.value) {
      await updateUnofficialCampaign(campaignId.value, buildPayload());
      useAlert(t('CAMPAIGN.UNOFFICIAL.EDIT.SUCCESS_MESSAGE'));
    } else {
      await createUnofficialCampaign(buildPayload());
      useAlert(t('CAMPAIGN.UNOFFICIAL.CREATE.SUCCESS_MESSAGE'));
    }

    emit('saved');
    dialogRef.value?.close();
  } catch (error) {
    showFormAlert(
      'error',
      error?.response?.data?.message ||
        error?.message ||
        t('CAMPAIGN.UNOFFICIAL.ALERTS.SAVE_ERROR')
    );
  } finally {
    isSaving.value = false;
  }
};

const handleCancel = () => {
  dialogRef.value?.close();
};

const handleDialogClose = () => {
  emit('close');
};

const open = async (options = {}) => {
  mode.value = options.mode || 'create';
  campaignId.value = options.campaignId || null;
  resetState();
  isInitializing.value = true;

  await nextTick();
  dialogRef.value?.open();

  await loadInboxes();

  if (isEditMode.value && campaignId.value) {
    await loadCampaign(campaignId.value);
  }

  isInitializing.value = false;
};

defineExpose({ dialogRef, open });
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="5xl"
    overflow-y-auto
    show-close-icon
    :title="dialogTitle"
    :show-cancel-button="false"
    :show-confirm-button="false"
    @confirm="handleSubmit"
    @close="handleDialogClose"
  >
    <div v-if="isInitializing" class="flex items-center justify-center py-16">
      <Spinner />
    </div>

    <div v-else class="flex flex-col gap-6">
      <div
        v-if="isEditMode && campaignId"
        class="px-3 py-2 text-xs font-medium border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-11"
      >
        {{ t('CAMPAIGN.UNOFFICIAL.EDIT.CAMPAIGN_ID', { id: campaignId }) }}
      </div>

      <section class="space-y-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.DETAILS') }}
        </h3>

        <div class="grid gap-4 lg:grid-cols-2">
          <Input
            v-model="form.nome"
            :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.NAME.LABEL')"
            :placeholder="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.NAME.PLACEHOLDER')"
          />

          <Input
            v-model="form.horario_disparo"
            type="time"
            :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.SCHEDULE_TIME.LABEL')"
          />
        </div>
      </section>

      <section class="space-y-4">
        <div class="space-y-1">
          <h3 class="text-sm font-medium text-n-slate-12">
            {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.MESSAGE') }}
          </h3>
          <p class="text-xs text-n-slate-11">
            {{ t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.MESSAGE.HELP') }}
          </p>
        </div>

        <TextArea
          v-model="form.mensagem"
          auto-height
          :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.MESSAGE.LABEL')"
          :placeholder="
            t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.MESSAGE.PLACEHOLDER')
          "
        />

        <div v-if="detectedVariableKeys.length" class="space-y-2">
          <p class="mb-0 text-xs font-medium uppercase text-n-slate-11">
            {{ t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.DETECTED_VARIABLES') }}
          </p>

          <div class="flex flex-wrap gap-2">
            <span
              v-for="variable in detectedVariableKeys"
              :key="variable"
              class="inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full border-n-weak bg-n-solid-2 text-n-blue-text"
            >
              {{ formatVariableToken(variable) }}
            </span>
          </div>
        </div>
      </section>

      <section class="space-y-4">
        <div
          class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
        >
          <div class="space-y-1">
            <h3 class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.RECIPIENTS') }}
            </h3>
            <p class="text-xs text-n-slate-11">
              {{
                t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.COUNT', {
                  count: contacts.length,
                })
              }}
            </p>
          </div>

          <div class="flex flex-col gap-2 sm:flex-row">
            <Button
              :label="t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.BUTTONS.IMPORT_CSV')"
              variant="faded"
              color="slate"
              class="w-full sm:w-auto"
              type="button"
              @click="openCsvPicker"
            />
            <Button
              :label="
                t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.BUTTONS.DOWNLOAD_TEMPLATE')
              "
              variant="faded"
              color="slate"
              class="w-full sm:w-auto"
              type="button"
              @click="downloadTemplateCSV"
            />
            <input
              ref="csvInputRef"
              type="file"
              accept=".csv"
              hidden
              @change="handleCSVUpload"
            />
          </div>
        </div>

        <div
          v-if="!detectedVariableKeys.length"
          class="px-4 py-3 text-sm border rounded-lg border-n-weak bg-n-solid-2 text-n-slate-11"
        >
          {{ t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.EMPTY_HELP') }}
        </div>

        <div class="flex flex-wrap gap-2">
          <Button
            :label="t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.MODE.MANUAL')"
            :variant="!isCSVMode ? 'solid' : 'faded'"
            :color="!isCSVMode ? 'blue' : 'slate'"
            size="sm"
            type="button"
            @click="isCSVMode = false"
          />
          <Button
            :label="t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.MODE.CSV')"
            :variant="isCSVMode ? 'solid' : 'faded'"
            :color="isCSVMode ? 'blue' : 'slate'"
            size="sm"
            type="button"
            @click="isCSVMode = true"
          />
        </div>

        <div
          v-if="!isCSVMode"
          class="p-4 border rounded-xl border-n-weak bg-n-solid-2"
        >
          <div class="space-y-4">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{ t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.MANUAL_TITLE') }}
            </h4>

            <div class="grid gap-4 lg:grid-cols-2">
              <Input
                v-model="newContact.name"
                :label="
                  t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.CONTACT_NAME.LABEL')
                "
                :placeholder="
                  t(
                    'CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.CONTACT_NAME.PLACEHOLDER'
                  )
                "
              />

              <Input
                v-model="newContact.phone"
                type="tel"
                :label="t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.PHONE.LABEL')"
                :placeholder="
                  t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.PHONE.PLACEHOLDER')
                "
              />
            </div>

            <div
              v-if="detectedVariableKeys.length"
              class="grid gap-4 lg:grid-cols-2"
            >
              <Input
                v-for="variable in detectedVariableKeys"
                :key="variable"
                v-model="newContact.vars[variable]"
                :label="
                  t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.VARIABLE.LABEL', {
                    variable,
                  })
                "
                :placeholder="
                  t(
                    'CAMPAIGN.UNOFFICIAL.RECIPIENTS.FIELDS.VARIABLE.PLACEHOLDER',
                    { variable }
                  )
                "
              />
            </div>

            <div class="flex justify-end">
              <Button
                icon="i-lucide-plus"
                :label="t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.BUTTONS.ADD_CONTACT')"
                class="w-full sm:w-auto"
                type="button"
                @click="addContact"
              />
            </div>
          </div>
        </div>

        <div class="space-y-3">
          <article
            v-for="(contact, index) in contacts"
            :key="`${contact.phone}-${index}`"
            class="flex flex-col gap-3 p-4 border rounded-xl border-n-weak bg-n-solid-1 sm:flex-row sm:items-start sm:justify-between"
          >
            <div class="min-w-0 space-y-2">
              <div>
                <p class="mb-1 text-sm font-medium break-words text-n-slate-12">
                  {{
                    contact.name || t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.NO_NAME')
                  }}
                </p>
                <p class="mb-0 text-xs break-all text-n-slate-11">
                  {{ contact.phone }}
                </p>
              </div>

              <div
                v-if="Object.keys(contact.vars || {}).length"
                class="flex flex-wrap gap-2"
              >
                <span
                  v-for="(value, key) in contact.vars"
                  :key="key"
                  class="inline-flex items-center px-2.5 py-1 text-xs font-medium border rounded-full border-n-weak bg-n-solid-2 text-n-slate-12"
                >
                  {{ formatContactVariableToken(key, value) }}
                </span>
              </div>

              <p
                v-if="!checkContactVariablesComplete(contact)"
                class="mb-0 text-xs font-medium text-n-ruby-11"
              >
                {{ t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.INCOMPLETE_VARIABLES') }}
              </p>
            </div>

            <Button
              icon="i-lucide-trash-2"
              color="ruby"
              variant="faded"
              size="sm"
              class="self-start"
              type="button"
              :aria-label="
                t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.BUTTONS.REMOVE_CONTACT')
              "
              @click="removeContact(index)"
            />
          </article>

          <p
            v-if="!contacts.length"
            class="px-4 py-6 mb-0 text-sm text-center border rounded-xl border-dashed border-n-weak text-n-slate-11"
          >
            {{ t('CAMPAIGN.UNOFFICIAL.RECIPIENTS.LIST.EMPTY') }}
          </p>
        </div>
      </section>

      <section class="space-y-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.SETTINGS') }}
        </h3>

        <div class="grid gap-4 lg:grid-cols-2">
          <Input
            v-model="form.dt_inicio"
            type="datetime-local"
            :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.START_AT.LABEL')"
          />

          <Input
            v-model="form.dt_fim"
            type="datetime-local"
            :min="form.dt_inicio || undefined"
            :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.END_AT.LABEL')"
          />
        </div>

        <div class="grid gap-4 lg:grid-cols-2">
          <Input
            v-model="form.account"
            disabled
            :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.ACCOUNT.LABEL')"
          />

          <div class="flex flex-col gap-1">
            <label
              for="unofficial-campaign-inbox"
              class="mb-0.5 text-sm font-medium text-n-slate-12"
            >
              {{ t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.INBOX.LABEL') }}
            </label>

            <select
              id="unofficial-campaign-inbox"
              v-model="form.inbox"
              class="block w-full h-10 px-3 text-sm transition-all duration-500 ease-in-out border rounded-lg outline-none border-n-weak bg-n-alpha-black2 text-n-slate-12 hover:border-n-slate-6 focus:border-n-brand"
            >
              <option value="">
                {{ t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.INBOX.PLACEHOLDER') }}
              </option>
              <option
                v-for="inbox in inboxes"
                :key="inbox.id"
                :value="String(inbox.id)"
              >
                {{ inbox.name }}
              </option>
            </select>
          </div>
        </div>
      </section>

      <section class="space-y-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.MEDIA') }}
        </h3>

        <Input
          v-model="form.link_media"
          type="url"
          :label="t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.MEDIA_LINK.LABEL')"
          :placeholder="
            t('CAMPAIGN.UNOFFICIAL.FORM.FIELDS.MEDIA_LINK.PLACEHOLDER')
          "
          @input="handleMediaLinkChange"
        />
      </section>

      <section class="space-y-4">
        <h3 class="text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.UNOFFICIAL.FORM.SECTIONS.DAYS') }}
        </h3>

        <div class="flex flex-wrap gap-2">
          <Button
            v-for="day in weekdayOptions"
            :key="day.key"
            :label="day.label"
            :variant="hasSelectedDay(day.key) ? 'solid' : 'faded'"
            :color="hasSelectedDay(day.key) ? 'blue' : 'slate'"
            size="sm"
            type="button"
            @click="toggleDay(day.key)"
          />
        </div>
      </section>

      <div
        v-if="formAlert.message"
        class="px-4 py-3 text-sm border rounded-lg"
        :class="formAlertClass"
      >
        {{ formAlert.message }}
      </div>

      <div class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
        <Button
          :label="t('CAMPAIGN.UNOFFICIAL.BUTTONS.CANCEL')"
          variant="faded"
          color="slate"
          class="w-full sm:w-auto"
          type="button"
          @click="handleCancel"
        />
        <Button
          :label="submitLabel"
          class="w-full sm:w-auto"
          type="submit"
          :is-loading="isSaving"
          :disabled="isBusy"
        />
      </div>
    </div>

    <template #footer />
  </Dialog>
</template>
